import { Migration } from '@gleamer/types';
import React, { useEffect, useState } from 'react';
import { useMigrationCatchUpChanges } from '../../../../hooks/useMigration';
import { PageButton } from '../../../atoms/PageButton';
import { TableBodyTd, TableHeadTh } from '../../../atoms/Table';
import { formatDateTime } from '../imports/format';
import {
  CatchUpPhaseBadge,
  CatchUpStatusBadge,
  CommandButton,
  formatCount,
  PHASE_MEANINGS,
  selectClassName,
} from './parts';

type MigrationRun = Migration.MigrationRun;
type MigrationCatchUp = Migration.MigrationCatchUp;
type MigrationCatchUpStatus = Migration.MigrationCatchUpStatus;

const { MIGRATION_CATCH_UP_STATUSES } = Migration;

export const MIGRATION_CATCH_UP_CHANGES_PAGE_SIZE = 50;

export const STOP_CATCH_UP_CONFIRM =
  'Stop the catch-up? Do it at the freeze, once g3d is stopped and the backlog drained: changes after it are not applied.';

/** What the status filter offers, each with what it asks viewer-api for. */
const SHOWN: { value: string; label: string; statuses?: MigrationCatchUpStatus[] }[] = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending', statuses: ['PENDING'] },
  { value: 'FAILED', label: 'Failed', statuses: ['FAILED'] },
  { value: 'CONFLICT', label: 'Conflicts', statuses: ['CONFLICT'] },
  { value: 'APPLIED', label: 'Applied', statuses: ['APPLIED'] },
];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="font-bold text-dl-text">{label}</dt>
      <dd className="break-all">{children}</dd>
    </>
  );
}

/** Stops the run's catch-up alone: offered only while it follows. */
export function StopCatchUp({ run }: { run: MigrationRun }) {
  if (run.catchUp?.state !== 'FOLLOWING') {
    return null;
  }
  return (
    <CommandButton
      label="Stop the catch-up"
      confirm={STOP_CATCH_UP_CONFIRM}
      command={{ type: 'STOP_CATCH_UP', runId: run.id }}
    />
  );
}

/** The changes counted by kind and status: where the backlog, the failures and the conflicts are. */
function Counts({ catchUp }: { catchUp: MigrationCatchUp }) {
  const kinds = Array.from(new Set(catchUp.counts.map(c => c.kind)));
  if (kinds.length === 0) {
    return null;
  }
  const count = (kind: string, status: MigrationCatchUpStatus) =>
    catchUp.counts.find(c => c.kind === kind && c.status === status)?.count ?? 0;
  return (
    <table
      aria-label="Changes by kind"
      className="mb-4 w-full table-auto"
    >
      <thead className="bg-dl-track">
        <tr>
          <TableHeadTh>Kind</TableHeadTh>
          {MIGRATION_CATCH_UP_STATUSES.map(status => (
            <TableHeadTh key={status}>{status}</TableHeadTh>
          ))}
        </tr>
      </thead>
      <tbody>
        {kinds.map(kind => (
          <tr key={kind}>
            <TableBodyTd className="font-bold">{kind}</TableBodyTd>
            {MIGRATION_CATCH_UP_STATUSES.map(status => (
              <TableBodyTd
                key={status}
                className={
                  count(kind, status) > 0 && status === 'FAILED'
                    ? 'text-dl-danger'
                    : count(kind, status) > 0 && status === 'CONFLICT'
                      ? 'text-dl-warning'
                      : undefined
                }
              >
                {formatCount(count(kind, status))}
              </TableBodyTd>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * A run's catch-up: what g3d's events named since the run started. It piles them while the run copies, applies them
 * once it is done, then applies them as they come, until stopped at the freeze. A failed change is retried on its own
 * (after 30 s, doubling, at most 1 h) and never given up; a conflict is refused, since applying it would undo work
 * done on the new platform.
 */
export function MigrationCatchUpSection({ run }: { run: MigrationRun }) {
  const catchUp = run.catchUp;
  const [shown, setShown] = useState('');
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(0);
  const { data, status, error } = useMigrationCatchUpChanges(String(run.id), {
    page,
    size: MIGRATION_CATCH_UP_CHANGES_PAGE_SIZE,
    statuses: SHOWN.find(s => s.value === shown)?.statuses,
    kind: kind || undefined,
  });

  // changes applied meanwhile can empty the page shown: go back to the last page that has rows
  useEffect(() => {
    if (status === 'success' && data.items.length === 0 && page > 0) {
      setPage(Math.max(0, Math.min(page - 1, data.pages - 1)));
    }
  }, [status, data, page]);

  if (!catchUp) {
    return null;
  }
  const kinds = Array.from(new Set(catchUp.counts.map(c => c.kind)));

  return (
    <section
      aria-labelledby="catch-up-title"
      className="mb-4"
    >
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <h3
          id="catch-up-title"
          className="text-xl font-bold"
        >
          Catch-up
        </h3>
        <CatchUpPhaseBadge phase={catchUp.phase} />
        <span className="mr-auto text-sm text-dl-muted">{catchUp.state}</span>
        <StopCatchUp run={run} />
      </div>
      <p className="mb-3 text-dl-text">{PHASE_MEANINGS[catchUp.phase]}</p>
      <dl
        className="mb-4 grid gap-x-4 gap-y-1"
        style={{ gridTemplateColumns: 'max-content 1fr' }}
      >
        {catchUp.stoppedAt && (
          <Field label="Stopped">
            {formatDateTime(catchUp.stoppedAt)}
            {catchUp.stoppedBy && `, by ${catchUp.stoppedBy}`}
          </Field>
        )}
        <Field label="Backlog">{formatCount(catchUp.pending)}</Field>
        <Field label="Failed, retried">
          <span className={catchUp.failed > 0 ? 'text-dl-danger' : undefined}>
            {formatCount(catchUp.failed)}
          </span>
        </Field>
        <Field label="Conflicts">
          <span className={catchUp.conflicts > 0 ? 'text-dl-warning' : undefined}>
            {formatCount(catchUp.conflicts)}
          </span>
        </Field>
        <Field label="Applied">{formatCount(catchUp.applied)}</Field>
        <Field label="Oldest pending">{formatDateTime(catchUp.oldestPending)}</Field>
        <Field label="Last applied">{formatDateTime(catchUp.lastApplied)}</Field>
        <Field label="Last 10 minutes">
          {formatCount(catchUp.arrivedRecently)} arrived, {formatCount(catchUp.appliedRecently)}{' '}
          applied
        </Field>
      </dl>

      <Counts catchUp={catchUp} />

      <div className="mb-2 flex flex-wrap items-end gap-4">
        <h4 className="mr-auto text-lg font-bold">Changes</h4>
        <label className="flex flex-col text-sm">
          Kind
          <select
            className={selectClassName}
            value={kind}
            onChange={e => {
              setKind(e.target.value);
              setPage(0);
            }}
          >
            <option value="">All</option>
            {kinds.map(k => (
              <option
                key={k}
                value={k}
              >
                {k}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-sm">
          Status
          <select
            className={selectClassName}
            value={shown}
            onChange={e => {
              setShown(e.target.value);
              setPage(0);
            }}
          >
            {SHOWN.map(s => (
              <option
                key={s.value}
                value={s.value}
              >
                {s.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {status === 'loading' && <div>Loading…</div>}
      {status === 'error' && (
        <div
          role="alert"
          className="text-dl-danger"
        >
          Could not load the changes: {(error as Error)?.message}
        </div>
      )}
      {status === 'success' && data.items.length === 0 && <div>No change.</div>}
      {status === 'success' && data.items.length > 0 && (
        <>
          <table className="w-full table-auto">
            <thead className="bg-dl-track">
              <tr>
                <TableHeadTh>Entity</TableHeadTh>
                <TableHeadTh>Kind</TableHeadTh>
                <TableHeadTh>Status</TableHeadTh>
                <TableHeadTh>Seen</TableHeadTh>
                <TableHeadTh>Last seen</TableHeadTh>
                <TableHeadTh>Attempts</TableHeadTh>
                <TableHeadTh>Error or reason</TableHeadTh>
              </tr>
            </thead>
            <tbody>
              {data.items.map(change => (
                <tr key={`${change.entity}/${change.firstSeenAt}`}>
                  <TableBodyTd
                    className="break-all font-mono text-sm"
                    title={`${change.operation}, first seen ${formatDateTime(change.firstSeenAt)}`}
                  >
                    {change.entity}
                  </TableBodyTd>
                  <TableBodyTd>{change.kind}</TableBodyTd>
                  <TableBodyTd>
                    <CatchUpStatusBadge status={change.status} />
                    {change.appliedAt && (
                      <div className="text-sm text-dl-muted">{formatDateTime(change.appliedAt)}</div>
                    )}
                  </TableBodyTd>
                  <TableBodyTd>{formatCount(change.timesSeen)}×</TableBodyTd>
                  <TableBodyTd className="whitespace-nowrap">{formatDateTime(change.seenAt)}</TableBodyTd>
                  <TableBodyTd>
                    {formatCount(change.attempts)}
                    {change.status === 'FAILED' && change.notBefore && (
                      <div className="text-sm text-dl-muted">
                        next try {formatDateTime(change.notBefore)}
                      </div>
                    )}
                  </TableBodyTd>
                  <TableBodyTd
                    className={
                      change.status === 'CONFLICT' ? 'text-sm text-dl-warning' : 'text-sm text-dl-danger'
                    }
                  >
                    {change.error}
                  </TableBodyTd>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center py-2">
            <span className="mr-2">
              Page {data.page + 1} of {Math.max(1, data.pages)} ({data.total} changes)
            </span>
            <PageButton
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Previous page
            </PageButton>
            <PageButton
              onClick={() => setPage(p => p + 1)}
              disabled={page + 1 >= data.pages}
            >
              Next page
            </PageButton>
          </div>
        </>
      )}
    </section>
  );
}
